/**
 * Hook pour determiner le type de profil de l'utilisateur connecte
 * Utilise pour l'affichage conditionnel dans la page Profil unifiee
 */
import { useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';

export interface ProfilType {
  /** L'utilisateur est connecte avec un compte Accord Cadre */
  isAccordCadre: boolean;
  /** L'utilisateur est connecte avec un compte Prestataire Libre */
  isPrestataireLibre: boolean;
  /** L'utilisateur est un Acheteur */
  isAcheteur: boolean;
  /** L'utilisateur peut editer les lots techniques */
  canEditLots: boolean;
  /** L'utilisateur peut editer les infos bancaires */
  canEditBancaire: boolean;
  /** Afficher la barre de progression */
  showProgression: boolean;
  /** FRONT-09: Afficher la section lots */
  showLots: boolean;
  /** L'utilisateur est admin de son type */
  isAdmin: boolean;
  /** Role brut de l'utilisateur */
  role: string;
}

/**
 * Roles Accord Cadre (titulaires de marches)
 */
const ACCORD_CADRE_ROLES = [
  'gestionnaire',
  'interlocuteur',
  'prestataire signataire admin',
  'prestataire signataire',
  'prestataire',
];

/**
 * Roles Prestataire Libre (hors marches)
 */
const PRESTATAIRE_LIBRE_ROLES = [
  'prestataire_libre',
  'prestataire_libre_admin',
  'prestataire libre',
  'prestataire libre admin',
];

/**
 * Roles Acheteur
 */
const ACHETEUR_ROLES = [
  'acheteur',
  'acheteur signataire',
  'acheteur signataire admin',
];

/**
 * Roles admin
 */
const ADMIN_ROLES = [
  'gestionnaire',
  'prestataire_libre_admin',
  'prestataire signataire admin',
  'prestataire libre admin',
  'acheteur signataire admin',
];

/**
 * Hook pour determiner le type de profil
 */
export const useProfilType = (): ProfilType => {
  const { user } = useAuth();
  
  return useMemo(() => {
    const role = user?.role?.toLowerCase() || '';
    
    // Detection du type basee sur le role
    const isPrestataireLibre = PRESTATAIRE_LIBRE_ROLES.some(r => 
      role.includes(r.toLowerCase())
    );
    
    const isAcheteur = ACHETEUR_ROLES.some(r => 
      role.includes(r.toLowerCase())
    );
    
    const isAccordCadre = !isPrestataireLibre && !isAcheteur && ACCORD_CADRE_ROLES.some(r => 
      role.includes(r.toLowerCase())
    );
    
    const isAdmin = ADMIN_ROLES.some(r => 
      role.includes(r.toLowerCase())
    );
    
    return {
      isAccordCadre,
      isPrestataireLibre,
      isAcheteur,
      canEditLots: isPrestataireLibre || isAccordCadre, // FRONT-09: AC peut modifier sauf lots titulaires
      canEditBancaire: isPrestataireLibre || isAccordCadre, // CORE-15: AC peut aussi editer bancaire
      showProgression: isPrestataireLibre || isAccordCadre, // CORE-15: AC voit aussi la progression
      showLots: isPrestataireLibre || isAccordCadre, // FRONT-09: AC voit les lots
      isAdmin,
      role: user?.role || '',
    };
  }, [user?.role]);
};

export default useProfilType;
